import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ALL_NAV_ITEMS, getActiveNavId } from '../components/therapist/layout/navConfig';

/**
 * Therapist UI context — "where am I, and which client am I looking at?"
 *
 * The therapist chrome (TopBar) owns the Help / Report-an-issue button, but
 * the interesting state lives far below it: TherapistDashboard holds the
 * selected client in React state (not the URL), ClientDetailView holds the
 * active tab, ConversationView holds the selected conversation. None of that
 * is derivable from the route, so surfaces *register* what they know here
 * and TopBar reads the merged result when a ticket is submitted.
 *
 * Usage (surface side):
 *   useTherapistUiContextRegistration('clientDetail', { clientId, tabLabel });
 *   // → section is added while mounted and removed on unmount
 *
 * Usage (reader side):
 *   const { getReportContext } = useTherapistUiContext();
 *   const ctx = getReportContext(); // → SupportTicket.context-shaped object
 *
 * Both hooks are safe to call without a provider (no-op / route-only
 * context) so components stay renderable in isolation and in tests.
 */

const NOOP_CONTEXT = {
  sections: {},
  setSection: () => {},
  clearSection: () => {},
};

const TherapistUiContext = createContext(NOOP_CONTEXT);

export function TherapistUiProvider({ children }) {
  const [sections, setSections] = useState({});

  const setSection = useCallback((key, value) => {
    setSections((prev) => (prev[key] === value ? prev : { ...prev, [key]: value }));
  }, []);

  const clearSection = useCallback((key) => {
    setSections((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const value = useMemo(() => ({ sections, setSection, clearSection }), [sections, setSection, clearSection]);

  return <TherapistUiContext.Provider value={value}>{children}</TherapistUiContext.Provider>;
}

/**
 * Register a named section of UI context for as long as the calling
 * component is mounted. Re-registers only when the serialised value changes,
 * so callers can pass a fresh object literal on every render.
 */
export function useTherapistUiContextRegistration(key, value) {
  const { setSection, clearSection } = useContext(TherapistUiContext);
  const serialised = safeStringify(value);
  const latest = useRef(value);
  latest.current = value;

  // Re-register whenever the serialised value changes (callers pass a fresh
  // object literal every render, so the deps array tracks the string form).
  // The effect also re-runs after StrictMode's dev-only unmount/remount,
  // which is what restores the section after the cleanup below cleared it.
  React.useEffect(() => {
    setSection(key, latest.current);
  }, [key, serialised, setSection]);

  React.useEffect(() => () => clearSection(key), [key, clearSection]);
}

/**
 * Read the merged context. `getReportContext()` returns an object shaped for
 * SupportTicket.context (see server/models/SupportTicket.js).
 */
export function useTherapistUiContext() {
  const { sections } = useContext(TherapistUiContext);
  const location = useLocation();
  const getReportContext = useCallback(
    () => buildTherapistReportContext(location, sections),
    [location, sections]
  );
  return { sections, getReportContext };
}

// ---------------------------------------------------------------------------
// Pure mapping: (route, registered sections) → SupportTicket.context fields.
// Kept free of React so it can be unit-tested directly.
// ---------------------------------------------------------------------------

const NAV_LABELS = ALL_NAV_ITEMS.reduce((acc, item) => {
  acc[item.id] = item.label;
  return acc;
}, {});

const CLIENT_LIST_VIEWS = new Set(['clients', 'training-clients']);

/**
 * @param {{ pathname: string, search?: string }} location
 * @param {object} sections - registered sections keyed by name:
 *   dashboard    { activeView, selectedClientId, selectedClientName, selectedClientKind }
 *   clientDetail { clientId, clientKind, tabLabel, latestConversationId }
 *   conversation { conversationId, controlMode }
 *   consultation { consultationId, clientId, conversationId }
 * @returns {object} SupportTicket.context-shaped fields (undefined values omitted)
 */
export function buildTherapistReportContext(location, sections = {}) {
  const path = (location && location.pathname) || '';
  const dashboard = sections.dashboard || {};
  const clientDetail = sections.clientDetail || {};
  const conversation = sections.conversation || {};
  const consultation = sections.consultation || {};

  const crumbs = [];
  let currentPage = 'other';
  let dashboardTab;

  if (path.startsWith('/therapist/dashboard')) {
    const navId = getActiveNavId(location) || dashboard.activeView || 'dashboard';
    dashboardTab = navId;
    crumbs.push(NAV_LABELS[navId] || navId);
    if (CLIENT_LIST_VIEWS.has(navId)) {
      currentPage = dashboard.selectedClientId || clientDetail.clientId ? 'therapist_client_detail' : 'therapist_clients';
    } else {
      currentPage = 'therapist_dashboard';
    }
  } else if (path.startsWith('/therapist/client/')) {
    currentPage = 'therapist_client_detail';
    crumbs.push('Clients');
  } else if (path.startsWith('/therapist/reviews')) {
    currentPage = 'therapist_reviews';
    crumbs.push(NAV_LABELS['client-reviews']);
  } else if (path.startsWith('/therapist/simulation-lab')) {
    currentPage = 'therapist_simulation_lab';
    crumbs.push(NAV_LABELS['simulation-lab']);
    if (path.includes('/history')) crumbs.push('History');
  } else if (path.startsWith('/therapist/pre-session')) {
    currentPage = 'therapist_pre_session';
    crumbs.push(NAV_LABELS['pre-session']);
  } else if (path.startsWith('/therapist/practice')) {
    currentPage = 'therapist_practice';
    crumbs.push(NAV_LABELS['practice-settings']);
  } else if (path.startsWith('/supervisor/consultation')) {
    currentPage = 'therapist_consultation';
    crumbs.push('AI Supervisor Consultation');
  } else if (path.startsWith('/call/')) {
    currentPage = 'therapist_call';
    crumbs.push('Live video call');
  }

  // Which client is in view. ClientDetailView is the most specific source;
  // the dashboard's selection and a consultation's client are fallbacks.
  const viewingClientId = clientDetail.clientId || dashboard.selectedClientId || consultation.clientId || undefined;
  const viewingClientKind = clientDetail.clientKind || dashboard.selectedClientKind || undefined;

  if (currentPage === 'therapist_client_detail' && viewingClientId) {
    crumbs.push('Client detail');
    if (clientDetail.tabLabel) crumbs.push(clientDetail.tabLabel);
  }

  const conversationId = conversation.conversationId
    || consultation.conversationId
    || clientDetail.latestConversationId
    || undefined;

  let journeyStage = 'unknown';
  if (currentPage === 'therapist_call') journeyStage = 'therapy_call';
  else if (viewingClientKind === 'synthetic') journeyStage = 'practice';
  else if (viewingClientId) journeyStage = 'therapy';

  return stripUndefined({
    userRole: 'therapist',
    currentPage,
    journeyStage,
    uiLocation: crumbs.join(' › ') || undefined,
    dashboardTab,
    clientTab: currentPage === 'therapist_client_detail' ? clientDetail.tabLabel : undefined,
    viewingClientId,
    viewingClientKind,
    conversationId,
    conversationControlMode: conversation.conversationId ? conversation.controlMode : undefined,
    aiConsultationId: consultation.consultationId || undefined,
  });
}

function stripUndefined(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''));
}

function safeStringify(value) {
  try {
    return JSON.stringify(value);
  } catch (_) {
    return String(value);
  }
}

export default TherapistUiContext;
